import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { clearToken } from "../lib/auth";
import { api } from "../lib/api";

type Tab = "stats" | "users" | "spaces" | "waitlist";

interface Stats {
  totals: { users: number; spaces: number; pages: number; published_versions: number; waitlist: number };
  daily_users: { day: string; count: number }[];
  daily_spaces: { day: string; count: number }[];
}

interface AdminUser {
  id: string; username: string; email: string; display_name: string | null;
  created_at: string; space_count: number;
}

interface AdminSpace {
  id: string; username: string; slug: string; name: string; description: string | null;
  created_at: string; owner_email: string; page_count: number;
}

interface WaitlistEntry {
  id: string; email: string; created_at: string;
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("stats");

  const [stats, setStats] = useState<Stats | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [spaces, setSpaces] = useState<AdminSpace[]>([]);
  const [waitlist, setWaitlist] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const endpoint = tab === "stats" ? "/api/admin/stats"
      : tab === "users" ? "/api/admin/users"
      : tab === "spaces" ? "/api/admin/spaces"
      : "/api/admin/waitlist";

    api.get<any>(endpoint)
      .then((data) => {
        if (tab === "stats") setStats(data);
        else if (tab === "users") setUsers(data);
        else if (tab === "spaces") setSpaces(data);
        else setWaitlist(data);
      })
      .catch((e) => {
        if (e instanceof Error && e.message.includes("403")) {
          navigate("/dashboard", { replace: true });
        } else {
          setError(e instanceof Error ? e.message : "Failed to load");
        }
      })
      .finally(() => setLoading(false));
  }, [tab]);

  function handleSignOut() {
    clearToken();
    navigate("/", { replace: true });
  }

  const tabClass = (t: Tab) =>
    `px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
      tab === t
        ? "bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900"
        : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
    }`;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/" className="text-lg font-bold text-gray-900 dark:text-gray-100">Agent Wiki</Link>
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
            Admin
          </span>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/dashboard" className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100">
            Dashboard
          </Link>
          <button
            onClick={handleSignOut}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Admin Panel</h1>

        {/* Tabs */}
        <div className="flex gap-2 mb-8">
          {(["stats", "users", "spaces", "waitlist"] as Tab[]).map((t) => (
            <button key={t} onClick={() => setTab(t)} className={tabClass(t)}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {loading && (
          <div className="space-y-3 animate-pulse">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-12 bg-white dark:bg-gray-800 rounded-lg" />
            ))}
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {/* Stats */}
        {!loading && !error && tab === "stats" && stats && (
          <div className="space-y-8">
            {/* Totals */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              {Object.entries(stats.totals).map(([key, val]) => (
                <div key={key} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{val}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{key.replace(/_/g, " ")}</p>
                </div>
              ))}
            </div>

            {/* Daily registrations */}
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-5">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">Daily User Registrations</h3>
                <DailyTable rows={stats.daily_users} />
              </div>
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-5">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">Daily Space Creations</h3>
                <DailyTable rows={stats.daily_spaces} />
              </div>
            </div>
          </div>
        )}

        {/* Users */}
        {!loading && !error && tab === "users" && (
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{users.length} users total</p>
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Username</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Email</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Spaces</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Joined</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {users.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">@{u.username}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{u.email}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{u.space_count}</td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-500 text-xs">{u.created_at.slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Spaces */}
        {!loading && !error && tab === "spaces" && (
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{spaces.length} spaces total</p>
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Space</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Owner</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Pages</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {spaces.map((s) => (
                    <tr key={s.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-3">
                        <Link
                          to={`/${s.username}/${s.slug}`}
                          className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          @{s.username}/{s.slug}
                        </Link>
                        {s.description && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate max-w-xs">{s.description}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">{s.owner_email}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{s.page_count}</td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-500 text-xs">{s.created_at.slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Waitlist */}
        {!loading && !error && tab === "waitlist" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">{waitlist.length} emails collected</p>
              <button
                onClick={() => {
                  const csv = ["email,date", ...waitlist.map((w) => `${w.email},${w.created_at.slice(0, 10)}`)].join("\n");
                  const blob = new Blob([csv], { type: "text/csv" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url; a.download = "waitlist.csv"; a.click();
                }}
                className="text-xs px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Export CSV
              </button>
            </div>
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Email</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {waitlist.map((w) => (
                    <tr key={w.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{w.email}</td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-500 text-xs">{w.created_at.slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function DailyTable({ rows }: { rows: { day: string; count: number }[] }) {
  if (rows.length === 0) return <p className="text-sm text-gray-400">No data yet.</p>;
  return (
    <div className="space-y-1">
      {rows.map((r) => (
        <div key={r.day} className="flex items-center gap-3">
          <span className="text-xs text-gray-500 dark:text-gray-400 w-24 shrink-0">{r.day}</span>
          <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full"
              style={{ width: `${Math.min(100, (r.count / Math.max(...rows.map((x) => x.count))) * 100)}%` }}
            />
          </div>
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300 w-4 text-right">{r.count}</span>
        </div>
      ))}
    </div>
  );
}
